// Result presentation shared by the Test, Share and Compare pages: the headline
// metric tiles (with Wilson interval), the extended metric grid, and the full
// per-run chart suite. Kept separate from primitive components so pages can mix
// and match.

import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { NodeInfo, RunResult } from '@/engine/types';
import {
  BurstChart,
  JitterView,
  LossOverTime,
  PacketStrip,
  RttDistribution,
  RttOverTime,
} from '@/charts';
import { ms, pct } from '@/lib/format';
import { exportCanvasPNG, exportPacketsCSV, exportRunJSON } from '@/lib/export';
import { MetricTile, Panel, TierBadge } from './components';

/** Colour tone for a loss percentage. */
function lossTone(lossPct: number): 'ok' | 'warn' | 'bad' {
  if (lossPct <= 0.5) return 'ok';
  if (lossPct <= 5) return 'warn';
  return 'bad';
}

/** The six headline metrics with the Wilson confidence interval. */
export function HeadlineMetrics({ run }: { run: RunResult }) {
  const { t } = useTranslation();
  const m = run.metrics;
  return (
    <div className="tilegrid tilegrid--headline">
      <MetricTile
        label={t('metrics.totalLoss')}
        value={pct(m.totalLossPct)}
        sub={t('metrics.wilson', {
          low: m.wilsonLossLow.toFixed(2),
          high: m.wilsonLossHigh.toFixed(2),
        })}
        tone={lossTone(m.totalLossPct)}
      />
      <MetricTile
        label={t('metrics.uploadLoss')}
        value={pct(m.uploadLossPct)}
        tone={lossTone(m.uploadLossPct)}
      />
      <MetricTile
        label={t('metrics.downloadLoss')}
        value={pct(m.downloadLossPct)}
        tone={lossTone(m.downloadLossPct)}
      />
      <MetricTile label={t('metrics.late')} value={pct(m.latePct)} tone={m.latePct > 2 ? 'warn' : 'neutral'} />
      <MetricTile label={t('metrics.avgLatency')} value={ms(m.avgLatencyMs)} />
      <MetricTile
        label={t('metrics.jitter')}
        value={ms(m.rfc3550JitterMs)}
        sub={`p99 ${ms(m.p99)}`}
      />
    </div>
  );
}

/** Extended metric grid (percentiles, drift, bursts). */
export function ExtendedMetrics({ run }: { run: RunResult }) {
  const { t } = useTranslation();
  const m = run.metrics;
  return (
    <div className="tilegrid">
      <MetricTile label={t('metrics.p50')} value={ms(m.p50)} />
      <MetricTile label={t('metrics.p90')} value={ms(m.p90)} />
      <MetricTile label={t('metrics.p99')} value={ms(m.p99)} />
      <MetricTile label={t('metrics.minRtt')} value={ms(m.minRtt)} />
      <MetricTile label={t('metrics.maxRtt')} value={ms(m.maxRtt)} />
      <MetricTile
        label={t('metrics.drift')}
        value={`${m.driftMsPerS.toFixed(2)} ms/s`}
        tone={m.driftMsPerS > 1 ? 'warn' : 'neutral'}
      />
    </div>
  );
}

/** One captioned chart with a PNG export button. */
function ChartCard({ title, children }: { title: ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  return (
    <div className="chartcard">
      <div className="chartcard__head">
        <h3 className="chartcard__title">{title}</h3>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() =>
            exportCanvasPNG(ref.current, `plm-chart-${String(title).toLowerCase().replace(/\s+/g, '-')}.png`)
          }
        >
          {t('common.exportPng')}
        </button>
      </div>
      <div className="chartcard__body" ref={ref}>
        {children}
      </div>
    </div>
  );
}

/** The full per-run chart suite. */
export function RunCharts({ run }: { run: RunResult }) {
  const { t } = useTranslation();
  return (
    <div className="chartgrid">
      <ChartCard title={t('charts.rttOverTime')}>
        <RttOverTime run={run} />
      </ChartCard>
      <ChartCard title={t('charts.lossOverTime')}>
        <LossOverTime run={run} />
      </ChartCard>
      <ChartCard title={t('charts.distribution')}>
        <RttDistribution run={run} />
      </ChartCard>
      <ChartCard title={t('charts.jitter')}>
        <JitterView run={run} />
      </ChartCard>
      <ChartCard title={t('charts.burst')}>
        <BurstChart run={run} />
      </ChartCard>
      <ChartCard title={t('charts.packetStrip')}>
        <PacketStrip run={run} />
      </ChartCard>
    </div>
  );
}

/** How the tester reached this node: address family, the client's own public IP,
 *  and the network (ASN + operator) \u2014 the identity we record per test. Rendered as
 *  a prominent labelled strip, not a footnote. */
function ClientInfoStrip({ info }: { info: NonNullable<RunResult['clientInfo']> }) {
  const { t } = useTranslation();
  const familyLabel = info.family === 'ipv4' ? 'IPv4' : info.family === 'ipv6' ? 'IPv6' : null;
  const hasNetwork = !!info.asn && info.asn > 0;
  if (!familyLabel && !info.clientIp && !hasNetwork) return null;
  return (
    <div className="clientinfo" aria-label={t('clientInfo.title')}>
      <div className="clientinfo__item">
        <span className="clientinfo__label">{t('clientInfo.ip')}</span>
        <span className="clientinfo__value">
          {familyLabel ? (
            <span className={`badge badge--fam badge--${info.family}`}>{familyLabel}</span>
          ) : null}
          <span className="clientinfo__ip">{info.clientIp || '\u2014'}</span>
        </span>
      </div>
      <div className="clientinfo__item">
        <span className="clientinfo__label">{t('clientInfo.network')}</span>
        <span className="clientinfo__value">
          {hasNetwork ? (
            <>
              <span className="clientinfo__asn">AS{info.asn}</span>
              {info.asnOrg ? <span className="clientinfo__org">{info.asnOrg}</span> : null}
            </>
          ) : (
            <span className="muted">{t('clientInfo.unknownNetwork')}</span>
          )}
        </span>
      </div>
    </div>
  );
}

export function RunReport({
  run,
  node,
}: {
  run: RunResult;
  node?: NodeInfo;
}) {
  const { t } = useTranslation();
  const name = node?.name ?? run.nodeId;
  return (
    <Panel
      title={
        <span className="runhead">
          <TierBadge tier={run.tier} />
          <span>{name}</span>
        </span>
      }
      actions={
        <div className="btnrow">
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => exportPacketsCSV(run)}>
            {t('common.exportCsv')}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => exportRunJSON(run)}>
            {t('common.exportJson')}
          </button>
        </div>
      }
    >
      {run.clientInfo ? <ClientInfoStrip info={run.clientInfo} /> : null}
      <HeadlineMetrics run={run} />
      <ExtendedMetrics run={run} />
      <RunCharts run={run} />
    </Panel>
  );
}
